import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader as Loader2, Mail, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';

export const ResetPassword: React.FC = () => {
  const { publicWorkspace } = useWorkspace();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const branding = publicWorkspace?.appearance?.loginPage || {};
  
  const bgColor = branding.backgroundColor || '#18181b';
  const isLightBg = isLightColor(bgColor);

  const logo = branding.logo || publicWorkspace?.logo || (isLightBg ? '/icon-blue.svg' : '/icon-white.svg');

  const cardBg      = isLightBg ? 'rgba(255,255,255,0.85)' : 'rgba(39,39,42,0.85)';
  const cardBorder  = isLightBg ? 'rgba(0,0,0,0.10)'      : 'rgba(255,255,255,0.08)';
  const labelColor  = branding.textColor || (isLightBg ? '#1f2937' : '#e4e4e7');
  const inputBg     = isLightBg ? 'rgba(243,244,246,0.8)' : 'rgba(9,9,11,0.5)';
  const inputBorder = isLightBg ? 'rgba(0,0,0,0.15)'     : 'rgba(113,113,122,0.6)';
  const inputText   = isLightBg ? '#111827' : '#ffffff';
  const inputPlaceholder = isLightBg ? '#9ca3af' : '#71717a';
  const mutedText   = isLightBg ? 'rgba(55,65,81,0.7)' : '#a1a1aa';
  const accentColor = branding.buttonColor || '#3b82f6';
  const footerBorderColor = isLightBg ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)';

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      if (resetError) throw resetError;
      setSuccess(true);
      setEmail('');
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message);
      else setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-[100dvh] sm:h-auto sm:min-h-screen w-full overflow-y-auto flex flex-col p-4 transition-colors duration-500"
      style={{
        backgroundColor: bgColor,
        backgroundImage: branding.backgroundImage ? `url(${branding.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="w-full max-w-md m-auto py-6">
        {/* Logo + heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src={logo} alt={publicWorkspace?.name || 'AgencyPlus'} className="h-16 w-auto object-contain max-h-16" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: branding.textColor || (isLightBg ? '#111827' : '#ffffff') }}>
            Reset Password
          </h1>
          <p style={{ color: mutedText }}>Enter your email to receive a reset link</p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl shadow-xl overflow-hidden backdrop-blur-sm"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          {/* Card Header */}
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-xl font-semibold" style={{ color: labelColor }}>Forgot Password?</h2>
            <p className="text-sm mt-1" style={{ color: mutedText }}>No worries, we'll send you reset instructions</p>
          </div>

          {/* Card Body */}
          <div className="px-6 py-4">
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-900/20 border-green-700 text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    Password reset email sent! Check your inbox for further instructions.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <label htmlFor="email" className="block text-sm font-medium" style={{ color: labelColor }}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: inputPlaceholder }} />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-all"
                    style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: accentColor }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : 'Send Reset Link'}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div
            className="px-6 py-4"
            style={{ borderTop: `1px solid ${footerBorderColor}` }}
          >
            <p className="text-sm text-center" style={{ color: mutedText }}>
              Remember your password?{' '}
              <Link to="/login" className="font-semibold hover:opacity-80 transition-opacity" style={{ color: accentColor }}>
                Sign in
              </Link>
            </p>
            <p className="text-sm text-center mt-2">
              <Link to="/signup" className="hover:opacity-80 transition-opacity" style={{ color: mutedText }}>
                Don't have an account? Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Powered by Agency+ badge */}
        {publicWorkspace && (
          <p className="text-center text-sm mt-8 flex items-center justify-center gap-1" style={{ color: mutedText }}>
            Powered by{' '}
            <img src={isLightBg ? "/icon-blue.svg" : "/icon-white.svg"} alt="AgencyPlus" className="h-4 w-4 opacity-50" />
            <span className="font-semibold opacity-50 text-xs tracking-tight">AgencyPlus</span>
          </p>
        )}
      </div>
    </div>
  );
};

function isLightColor(color: string): boolean {
  try {
    let r = 0, g = 0, b = 0;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const full = hex.length === 3
        ? hex.split('').map(c => c + c).join('')
        : hex;
      r = parseInt(full.slice(0, 2), 16);
      g = parseInt(full.slice(2, 4), 16);
      b = parseInt(full.slice(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) { r = +match[0]; g = +match[1]; b = +match[2]; }
    }
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 160;
  } catch {
    return false;
  }
}
